import { isDefined } from '@sgme/fp';

import type { BlotterEntry } from '@/store/blotter/types/BlotterEntry.ts';
import type { OptionSide } from '@/store/option/state.ts';
import type { Side } from '@/store/orders/state.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';

import {
  type BlotterOrderEntryFromBackend,
  type BlotterOrderStatus,
  isAlgoOrderType,
  type OrderType,
} from '../api/types/BlotterOrderEntryFromBackend';

export type BlotterOrderEntry = BlotterEntry & {
  //
  // technical fields
  //
  updateTime: ISODateTime;

  // who
  clientId: number | undefined;

  // what
  orderType: OrderType;
  status: BlotterOrderStatus;

  currencyPair: CurrencyPair;
  yourWay: OptionSide | null;

  notional: number | null;
  notionalCurrency: Currency;
  executedAmount: number | null;
  marginInBps: number | null;
  margin: number | null;
  price: number | null;

  limitPrice: number;
  customerPrice: number | null;
  executionPrice: number | null;

  expiryDay: ISODate | null; // YYYY-MM-DD
  expiryTime: string | null; // hh:mm

  startTime: ISODateTime | null;
  endTime: ISODateTime | null;

  maturityDate: ISODateTime;

  liquidityPool: string | null;
  clippingMode: string | null;
  clipSize: number | null;

  swapPoints: number | null;
  forwardPrice: number | null;

  remainingAmount: number | null;
  noWorseThan: number | null;
  averagePrice: number | null;
  speed: string | null;

  fixingBenchmark: string | null;
  fixingPlace: string | null;
  fixingTime: string | null; // hh:mm:ss
  fixingDateUtc: ISODate | null; // YYYY-MM-DD
  fixingPriceType: string | null;
  fixingMarginType: string | null;

  // flags
  goodTillCancelled: boolean;
  randomize: boolean | null;
  alphaSeeker: boolean | null;
  spreadCapture: boolean | null;

  isGtc: boolean;
  isNdf: boolean;
};

/**
 * Converts an Order entry coming from webapi to a blotter order entry exploitable by the front.
 * @param order An order entry coming from webapi
 * @returns a blotter order entry exploitable by the front
 */
export const convertOrderEntry = (order: BlotterOrderEntryFromBackend): BlotterOrderEntry => {
  if (order.type !== 'BLOTTER.ORDER') {
    throw Error('dev error');
  }

  const clientId = Number.isNaN(Number(order.account)) ? undefined : Number.parseInt(order.account, 10);

  const isAlgo = isAlgoOrderType(order.orderType);

  return {
    ...order,
    clientId,
    margin:
      order.orderType === 'TakeProfit' ? getMarginWith(order.yourWay, order.limitPrice, order.customerPrice) : null,
    isGtc: order.goodTillCancelled,
    // strange logic (why did the server did not do it ?), but taken from legacy code
    goodTillCancelled: isAlgo ? false : order.goodTillCancelled,
  };
};

function getMarginWith(way: Side | null, limitPrice: number | null, customerPrice: number | null) {
  if (way !== null && limitPrice !== null && customerPrice !== null) {
    const firstPrice = way === 'Sell' ? limitPrice : customerPrice;
    const secondPrice = way === 'Sell' ? customerPrice : limitPrice;

    return Number.parseFloat(((firstPrice - secondPrice) * 10000).toFixed(5));
  }

  return null;
}

const STATUSES_OF_EDITABLE_ORDERS = ['Watched', 'Executing', 'Paused', 'Running', 'New'] as BlotterOrderStatus[];

export function orderIsEditable(orderEntry: BlotterOrderEntry | undefined): boolean {
  return (
    isDefined(orderEntry) &&
    isSgmeOrder(orderEntry) &&
    orderEntry.orderType !== 'Lyrebird' &&
    STATUSES_OF_EDITABLE_ORDERS.includes(orderEntry.status)
  );
}

function isSgmeOrder(orderEntry: BlotterOrderEntry): boolean {
  // taken from legacy code. Indeed there seems to be no other way
  return orderEntry.id.toUpperCase().startsWith('SGME');
}
